import { useEffect, useState } from 'react';
import { LayoutComponent } from '../LayoutComponent';
import { Cell, Column, Row, TableView, TableBody, TableHeader, Flex, Heading, Content, View, Link, ProgressCircle, DialogContainer, Dialog } from '@adobe/react-spectrum';
import moment from 'moment';
import { formatDecimalToString } from '../../../../utils/helperUtil';
import ProjectProductNewActivityAddEdit from './ProjectProductNewActivityAddEdit';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { ListProjectItemRequest } from '../../../../services/soap/item/requests/ListProjectItemRequest';
import { ListProjectItemRequestOptions_ProjectItem } from '../../../../services/soap/item/requests/ListProjectItemRequestOptions';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import { useTranslation } from 'react-i18next';
import { ToastQueue } from '@react-spectrum/toast';
import { GetItemRequest } from '../../../../services/soap/item/requests/GetItemRequest';
import useComonentReload from '../../../../hooks/UseComponentReload';
import styles from './project_product_new_activity_component.module.css';

interface ProjectProductProps {
  readonly attrib0ColumnName?: string;
  readonly attrib1ColumnName?: string;
  readonly attrib2ColumnName?: string;
  readonly attrib0ColumnVisible?: boolean;
  readonly attrib1ColumnVisible?: boolean;
  readonly attrib2ColumnVisible?: boolean;
  readonly billingDateColumnName?: string;
  readonly billingDateColumnVisible?: boolean;
  readonly componentTitle?: string;
  readonly minHeight?: number;
  readonly percentWidth?: number;
  readonly productType?: number;
  readonly quantityColumnName?: string;
  readonly quantityColumnVisible?: boolean;
  readonly serialNumberColumnName?: string;
  readonly serialNumberColumnVisible?: boolean;
  readonly serialNumberComponentIdent?: string;
  readonly unitPriceColumnName?: string;
  readonly unitPriceColumnVisible?: boolean;
  readonly supplierColumnVisible?: boolean;
  readonly selectedItem?: any;
  readonly isNew?: boolean;
  readonly setData?: (dataItems: any[]) => void;
  readonly isRequired?: boolean; // need for Create New Context
  readonly projectId?: number;
  readonly parentItem?: any;
}

export interface RowData {
  id: string;
  quantity: number;
  partID: string;
  _refId?: string;
  supplier?: string;
  supplierName?: string;
  partNumber: string;
  productName: string;
  currency: string;
  unitPrice: number;
  amountWithVat: number;
  vatValue: number;
  totalPrice: string;
  totalPriceWithVat: string;
  attrib0: string;
  attrib1: string;
  attrib2: string;
  refId: string;
  totalQuantity: number;
  amount: number;
  billingDate: string;
}

function ProjectProduct({
  attrib0ColumnName = 'Col.0',
  attrib1ColumnName = 'Col.1',
  attrib2ColumnName = 'Col.2',
  attrib0ColumnVisible = false,
  attrib1ColumnVisible = false,
  attrib2ColumnVisible = false,
  billingDateColumnName = 'Billing Date',
  billingDateColumnVisible = false,
  componentTitle = 'Project products',
  minHeight = 64,
  percentWidth = 100,
  productType = 2,
  quantityColumnName = 'Quantity',
  quantityColumnVisible = false,
  serialNumberColumnName = 'Serial Number',
  serialNumberColumnVisible = false,
  serialNumberComponentIdent = '',
  unitPriceColumnName = 'Unit Price',
  unitPriceColumnVisible = false,
  supplierColumnVisible = false,
  selectedItem,
  isNew = true,
  setData = () => {},
  isRequired,
  projectId,
  parentItem,
}: ProjectProductProps) {
  const { t } = useTranslation();
  const pageSize = 50;
  let defaultRowData: RowData = {
    id: '0',
    quantity: 1,
    partID: '',
    _refId: '',
    partNumber: '',
    productName: '',
    currency: '',
    unitPrice: 0,
    amountWithVat: 0,
    vatValue: 0,
    totalPrice: '0',
    totalPriceWithVat: '0',
    attrib0: '',
    attrib1: '',
    attrib2: '',
    refId: '',
    totalQuantity: 0,
    amount: 0,
    supplier: '',
    supplierName: '',
    billingDate: moment().toISOString(),
  };

  const [reloadComponent] = useComonentReload();
  const { itemService, store } = useDependency();
  const { isMobile } = useViewInfo();
  const [projectProductData, setProjectProductData] = useState<any>([]);
  const [rowsData, setRowsData] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<RowData>(defaultRowData);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showAddEditRow, setShowAddEditRow] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [columnsData, setColumnsData] = useState<any[]>([
    { name: 'Ref Id', key: 'partNumber', width: 120 },
    { name: 'Product', key: 'productName' },
    { name: 'Supplier', key: 'supplierName' },
    { name: attrib0ColumnName, key: 'attrib0' },
    { name: attrib1ColumnName, key: 'attrib1' },
    { name: attrib2ColumnName, key: 'attrib2' },
    { name: serialNumberColumnName, key: 'refId' },
    { name: quantityColumnName, key: 'quantity', width: 100 },
    { name: unitPriceColumnName, key: 'unitPriceCurr', width: 100 },
    { name: billingDateColumnName, key: 'billingDateFormatted' },
  ]);

  const [columnsDataMobile, setColumnsDataMobile] = useState<any[]>([
    { name: 'Ref Id', key: 'partNumber', width: 120 },
    { name: 'Product', key: 'productName' },
    { name: 'Supplier', key: 'supplierName' },
    { name: attrib0ColumnName, key: 'attrib0'},
    { name: attrib1ColumnName, key: 'attrib1'},
    { name: attrib2ColumnName, key: 'attrib2' },
    { name: serialNumberColumnName, key: 'refId' },
    { name: quantityColumnName, key: 'quantity', width: 120 },
    { name: unitPriceColumnName, key: 'unitPriceCurr', width: 120 },
    { name: billingDateColumnName, key: 'billingDateFormatted' },
  ]);

  useEffect(() => {
    // Define the columns to hide based on their visibility state
    const columnsToHide = [
      { key: 'supplierName', visible: supplierColumnVisible },
      { key: 'attrib0', visible: attrib0ColumnVisible },
      { key: 'attrib1', visible: attrib1ColumnVisible },
      { key: 'attrib2', visible: attrib2ColumnVisible },
      { key: 'refId', visible: serialNumberColumnVisible },
      { key: 'quantity', visible: quantityColumnVisible },
      { key: 'unitPriceCurr', visible: unitPriceColumnVisible },
      { key: 'billingDateFormatted', visible: billingDateColumnVisible },
    ];

    // Filter the columnsData array to exclude columns that are not visible
    const filteredColumnsData = columnsData.filter(column => columnsToHide.every(col => col.visible || col.key !== column.key));

    // Filter the columnsDataMobile array to exclude columns that are not visible
    const filteredColumnsDataMobile = columnsDataMobile.filter(column => columnsToHide.every(col => col.visible || col.key !== column.key));

    // Update the state with the filtered columns data
    setColumnsData(filteredColumnsData);
    setColumnsDataMobile(filteredColumnsDataMobile);
  }, [
    // Dependencies: the effect will run whenever any of these visibility states change
    attrib0ColumnVisible,
    attrib1ColumnVisible,
    attrib2ColumnVisible,
    serialNumberColumnVisible,
    quantityColumnVisible,
    unitPriceColumnVisible,
    billingDateColumnVisible,
    supplierColumnVisible,
  ]);

  useEffect(() => {
    setShowAddEditRow(false);
    //console.log('productType', productType);
    (async () => {
      (productType == 3 || productType == 4) && projectId && projectId > 0 && (await loadProjectProductData());
      showLoader && setShowLoader(false);
    })();
  }, [selectedItem, reloadComponent, projectId]);

  const updateProjectProductData = async (projectProductData: any[]) => {
    if (!projectProductData) return [];
    return Promise.all(
      projectProductData.map(async item => {
        const partNumber = await getPartNumber(item);
        return { ...item, _refId: `${partNumber}_${item.productName}_ref`, partNumber };
      })
    );
  };

  // Function to load project product data
  const loadProjectProductData = async () => {
    // Call API to fetch project product data
    let projecrProdRes: any = await itemService.listProjectItem(
      new ListProjectItemRequest(store.Server, store.SessionId, pageSize, 0, new ListProjectItemRequestOptions_ProjectItem('', `${projectId!}`, '', '3'))
    );

    // Set project product data state
    setProjectProductData(projecrProdRes);

    // If API call is successful
    if (projecrProdRes?.result === 'OK') {
      // Map the project product data to desired format
      let projectProductData = projecrProdRes?.ROWS?.map((item: any) => {
        return {
          id: item.ITEM.id,
          quantity: formatDecimalToString(Number.parseFloat(item.ITEM.quantity).toFixed(2)),
          partID: item.ITEM.item,
          _refId: '',
          partNumber: 'Part Number',
          productName: item.ITEM.name,
          supplier: item.ITEM.supplier,
          supplierName: item.ITEM.supplierName,
          currency: item.ITEM.currency,
          vatValue: item.ITEM.vatValue,
          amountWithVat: item.ITEM.amountWithVat,
          unitPrice: formatDecimalToString(item.ITEM.amount),
          unitPriceCurr: formatDecimalToString(item.ITEM.amount) + ' ' + item.ITEM.currency,
          totalPrice: formatDecimalToString(Number.parseFloat(item.ITEM.totalAmount).toFixed(2)),
          totalPriceCurr: formatDecimalToString(Number.parseFloat(item.ITEM.totalAmount).toFixed(2)) + ' ' + item.ITEM.currency,
          purchasePrice: item.ITEM.purchasePrice,
          totalAmountWithVat: item.ITEM.totalAmountWithVat,
          attrib0: item.ITEM.attrib0,
          attrib1: item.ITEM.attrib1,
          attrib2: item.ITEM.attrib2,
          refId: item.ITEM.refId,
          billingDate: item.ITEM.billingDate,
          billingDateFormatted: moment(item.ITEM.billingDate).format('MM/DD/YYYY'),
          isSelected: true,
        };
      });

      // Update project product data with additional information
      updateProjectProductData(projectProductData).then((updatedData: any) => {
        //console.log('updatedData', updatedData);
        setRowsData(updatedData);
        setData(updatedData);
        //console.log('rowsData', rowsData);
      });
      setShowLoader(false);
    }
  };

  const getPartNumber = async (item: any) => {
    //console.log('item', item);
    let itemResp = await itemService.getItem(new GetItemRequest(store.Server, store.SessionId, item.partID));
    //console.log('itemResp', itemResp);
    return itemResp.ITEM.refId;
  };

  const handleRowClick = (rowId: any): void => {
    // Hide the add/edit row initially
    setShowAddEditRow(false);

    // Close the dialog if on a mobile device
    if (isMobile) setIsOpen(false);

    // Check if all rows are selected or no rows are selected
    if (rowId === 'all' || (!rowId.currentKey && rowId.size === 0)) {
      // Update the selected keys state
      setSelectedKeys(rowId);

      // Set the isSelected property for each row based on the selection
      rowsData.forEach((item: any) => (item.isSelected = rowId === 'all'));

      // Update the data state based on the selection
      setData(rowId === 'all' ? rowsData : []);
    } else {
      // Convert the selected row IDs to an array
      const arrRowId = Array.from(rowId);

      // Filter the rows data to include only the selected rows
      const newRowsData = rowsData.filter((row: any) => arrRowId.includes(row.id));

      // Set the isSelected property for each selected row
      newRowsData.forEach((item: any) => (item.isSelected = true));

      // Find the data for the first selected row
      const rowData = rowsData.find((row: any) => row.id === arrRowId[0]);

      // Update the data state with the selected rows
      setData(rowsData);

      // Set the selected row data
      setSelectedRow(rowData);

      // Show the add/edit row
      setShowAddEditRow(true);

      // Open the dialog if on a mobile device
      if (isMobile) setIsOpen(true);

      // Update the selected keys state
      setSelectedKeys(rowId);
    }
  };

  const handleRowClose = (): void => {
    setSelectedRow(defaultRowData);
    setShowAddEditRow(false);
    isMobile && setIsOpen(false);
    //handleUnselect();
  };

  const handleUnselect = () => {
    setSelectedKeys(new Set());
  };

  const addRecord = async (record: any) => {
    try {
      // Check if the component is in "new" mode and if the setData function is provided
      if (isNew && setData) {
        // Create a new record with additional properties
        const updatedRowsData = {
          ...record,
          id: Math.random().toString(36).substring(7), // Generate a random ID for the new record
          unitPriceCurr: formatDecimalToString(record.unitPrice) + ' ' + record.currency, // Format the unit price with currency
          billingDateFormatted: moment(record.billingDate).format('MM/DD/YYYY'), // Format the billing date
          isSelecred: true, // Mark the record as selected (note: there is a typo here, it should be "isSelected")
        };

        // Filter the existing rows to include only the selected ones
        const filteredRowsData = rowsData.filter((row: any) => row.isSelected);
        // Add the new record to the filtered rows
        const newRowsData = [...filteredRowsData, updatedRowsData];
        // Add the new record to all rows
        const allRowsData = [...rowsData, updatedRowsData];

        // Update the state with the new rows data
        setRowsData(allRowsData);
        setData(allRowsData);

        // Update the selected keys with the IDs of the new rows
        const allKeys = new Set(newRowsData.map((row: any) => row.id));
        setSelectedKeys(allKeys);

        // Close the row editing view
        handleRowClose();
      }
    } catch (error: any) {
      // Show an error message if something goes wrong
      ToastQueue.negative(error.message, { timeout: 3000 });
    } finally {
      // Hide the loader
      setShowLoader(false);
    }
  };

  const updateRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        record.unitPriceCurr = formatDecimalToString(record.unitPrice) + ' ' + record.currency;
        record.billingDateFormatted = moment(record.billingDate).format('MM/DD/YYYY');
        const updatedRowsData = rowsData.map((row: any) => (row.id === record.id ? record : row));
        //console.log('updatedRowsData', updatedRowsData);
        setRowsData(updatedRowsData);
        setData(updatedRowsData);
        handleRowClose();
      }
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  const deleteRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        //console.log('record', record);
        const updatedRowsData = rowsData.filter((row: any) => row.id !== record.id);
        //console.log('updatedRowsData', updatedRowsData);
        setRowsData(updatedRowsData);
        setData(updatedRowsData);
        handleRowClose();
      }
    } catch (error) {
      console.log('deleteRecord Error', error);
    } finally {
      setShowLoader(false);
    }
  };

  const handleResult = async (result: any, successMessage: string) => {
    if (result.result === 'OK') {
      await loadProjectProductData();
      handleRowClose();
      ToastQueue.positive(successMessage, { timeout: 3000 });
    } else {
      ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
    }
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={'center'} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <View position={'relative'} width={'100%'} minHeight={'220px'}>
        <Flex direction={'column'} gap={'size-150'} position={'absolute'} top={0} left={0} right={0} width={'100%'} UNSAFE_style={{ zIndex: 1 }}>
          <Flex direction={'row'} alignItems={'start'} justifyContent={'start'}>
            <Content
              position={'relative'}
              UNSAFE_className={styles.heading_text}
              UNSAFE_style={{ border: isRequired && rowsData.length === 0 ? '1px solid red' : undefined, borderRadius: '5px', padding: '10px', width: 'auto' }}
            >
              {componentTitle}
            </Content>
            {isRequired && rowsData.length === 0 && (
              <Flex direction={'row'} UNSAFE_style={{ color: 'red', fontSize: '10px', padding: '15px' }}>
                {componentTitle.toLocaleLowerCase()} required
              </Flex>
            )}
            <Flex direction={'row'} alignItems={'center'} justifyContent={'center'} gap={'size-100'} UNSAFE_className={styles.icon_add_parent}>
              <Content>
                <Link
                  isQuiet
                  onPress={e => {
                    setSelectedRow(defaultRowData);
                    //handleUnselect();
                    isMobile && setIsOpen(true);
                    setShowAddEditRow(true);
                  }}
                >
                  <i className="bi bi-plus fs-5">
                    <View UNSAFE_className={styles.icon_add_text}>{t('add', { ns: 'layout_components' })}</View>
                  </i>
                </Link>
              </Content>
            </Flex>
          </Flex>
          <Flex direction={'column'} width={'100%'} UNSAFE_style={{ maxWidth: '100%' }}>

              {rowsData.length > 0 && (
                <TableView
                  UNSAFE_className="tblLayoutComponent"
                  aria-label="Project Product New Activity"
                  onSelectionChange={handleRowClick}
                  selectedKeys={selectedKeys}
                  defaultSelectedKeys="all"
                  selectionStyle="checkbox"
                  width={'100%'}
                  minHeight={'80px'}
                  maxHeight={{ base: '180px', L: '180px', M: '180px' }}
                  marginBottom={'size-250'}
                  selectionMode="multiple"
                  UNSAFE_style={{ maxWidth: '100%' }}
                >
                  <TableHeader columns={isMobile ? columnsDataMobile : columnsData}>
                    {column => (
                      <Column showDivider key={column.key} width={column?.width}>
                        {column.name}
                      </Column>
                    )}
                  </TableHeader>
                  <TableBody items={rowsData}>{item => <Row>{columnKey => <Cell>{(item as any)[String(columnKey)]}</Cell>}</Row>}</TableBody>
                </TableView>
              )}


          {isMobile ? (
            <DialogContainer
              isDismissable
              onDismiss={() => {
                setIsOpen(false);
              }}
            >
              {isOpen && (
                <Dialog>
                  <Heading> {selectedRow.id === '0' ? t('add', { ns: 'layout_components' }) : t('edit', { ns: 'layout_components' })}</Heading>
                  <Content>
                    <ProjectProductNewActivityAddEdit
                      id={String(selectedItem?.id)}
                      selectedItemType={selectedItem?.type}
                      defaultRowData={defaultRowData}
                      selectedRowData={selectedRow}
                      handleRowClose={handleRowClose}
                      addRecord={addRecord}
                      updateRecord={updateRecord}
                      deleteRecord={deleteRecord}
                      attrib0ColumnName={attrib0ColumnName}
                      attrib1ColumnName={attrib1ColumnName}
                      attrib2ColumnName={attrib2ColumnName}
                      attrib0ColumnVisible={attrib0ColumnVisible}
                      attrib1ColumnVisible={attrib1ColumnVisible}
                      attrib2ColumnVisible={attrib2ColumnVisible}
                      billingDateColumnName={billingDateColumnName}
                      billingDateColumnVisible={billingDateColumnVisible}
                      componentTitle={componentTitle}
                      quantityColumnName={quantityColumnName}
                      quantityColumnVisible={quantityColumnVisible}
                      serialNumberColumnName={serialNumberColumnName}
                      serialNumberColumnVisible={serialNumberColumnVisible}
                      unitPriceColumnName={unitPriceColumnName}
                      unitPriceColumnVisible={unitPriceColumnVisible}
                      supplierColumnVisible={supplierColumnVisible}
                      closeDialog={closeDialog}
                      styles={styles}
                    />
                  </Content>
                </Dialog>
              )}
            </DialogContainer>
          ) : (
            showAddEditRow && (
              <ProjectProductNewActivityAddEdit
                id={String(selectedItem?.id)}
                selectedItemType={selectedItem?.type}
                defaultRowData={defaultRowData}
                selectedRowData={selectedRow}
                handleRowClose={handleRowClose}
                addRecord={addRecord}
                updateRecord={updateRecord}
                deleteRecord={deleteRecord}
                attrib0ColumnName={attrib0ColumnName}
                attrib1ColumnName={attrib1ColumnName}
                attrib2ColumnName={attrib2ColumnName}
                attrib0ColumnVisible={attrib0ColumnVisible}
                attrib1ColumnVisible={attrib1ColumnVisible}
                attrib2ColumnVisible={attrib2ColumnVisible}
                billingDateColumnName={billingDateColumnName}
                billingDateColumnVisible={billingDateColumnVisible}
                componentTitle={componentTitle}
                quantityColumnName={quantityColumnName}
                quantityColumnVisible={quantityColumnVisible}
                serialNumberColumnName={serialNumberColumnName}
                serialNumberColumnVisible={serialNumberColumnVisible}
                unitPriceColumnName={unitPriceColumnName}
                unitPriceColumnVisible={unitPriceColumnVisible}
                supplierColumnVisible={supplierColumnVisible}
                closeDialog={closeDialog}
                styles={styles}
              />
            )
          )}
        </Flex>
        </Flex>
      </View>
    );
  }
}

export const ProjectProductNewActivityComponent = LayoutComponent(ProjectProduct);
